// src/lib/micro-propre.js — L'ANNULATION D'ÉCHO DU NAVIGATEUR, ET CE QU'ELLE
// PERMET VRAIMENT.
//
// ══ CE QUI A ÉTÉ MESURÉ (Chromium, 2026-09-09) ═════════════════════════════
//
// Dimitri : « regarde l'annulation d'écho du navigateur — c'est ce que font
// tous les appels vocaux ». Il a raison sur le principe. Mesuré :
//
//   getSupportedConstraints() → echoCancellation ✔ noiseSuppression ✔
//                               autoGainControl ✔
//   et getSettings() sur la piste obtenue les rend TOUS LES TROIS à `true` :
//   ce ne sont pas des vœux, ils sont APPLIQUÉS.
//
// ⚠️ MAIS — et c'est le point qui décide de toute l'architecture :
//
//   SpeechRecognition.prototype = start, stop, abort, lang, continuous,
//   interimResults, maxAlternatives, processLocally, phrases, + les
//   événements. RIEN D'AUTRE.
//
// Aucune entrée pour un flux. Pas de `stream`, pas de `source`, pas de
// `track`. **On ne peut pas lui donner l'audio nettoyé** : la reconnaissance
// vocale ouvre SA PROPRE capture, et on n'a aucune prise dessus. Ce n'est pas
// une limite de notre code, c'est la surface de l'API — vérifiée, pas
// supposée.
//
// ══ CE QU'ON EN FAIT, EN TROIS COUCHES ═════════════════════════════════════
//
// COUCHE 1 — ON OUVRE QUAND MÊME LE FLUX PROPRE, et on le garde ouvert.
//   Sur Chrome, l'entrée audio est traitée AU NIVEAU DU PÉRIPHÉRIQUE : une
//   session ouverte avec `echoCancellation` met le micro par défaut en mode
//   annulation. La reconnaissance, qui ouvre le même périphérique, peut en
//   hériter. ⚠️ Ce n'est PAS garanti par la spécification, et je ne peux pas
//   le prouver sans haut-parleurs réels. Alors on ne le suppose pas : on
//   COMPTE les échos, et la couche 3 tranche sur la mesure.
//
// COUCHE 2 — LA COUPURE PASSE PAR CE FLUX, PLUS PAR LA RECONNAISSANCE.
//   C'est la vraie réponse. Pour couper la parole, il n'est pas nécessaire de
//   COMPRENDRE : il suffit de savoir que quelqu'un parle. On mesure l'énergie
//   du flux NETTOYÉ — où le haut-parleur est annulé — et un dépassement
//   soutenu veut dire « un humain parle ». Donc : pendant qu'il parle, la
//   reconnaissance est FERMÉE (aucun écho possible, même dans le pire des cas)
//   et la coupure reste possible.
//
// COUCHE 3 — LE DEMI-DUPLEX, SUR PREUVE.
//   Si la détection se déclenche toute seule pendant qu'il parle (l'annulation
//   n'a pas suffi sur cette machine), on cesse d'écouter pendant qu'il parle,
//   et on le DIT. C'est le repli que Dimitri propose lui-même — appliqué
//   seulement quand la mesure le commande, jamais par précaution.

const CONTRAINTES = {
  audio: {
    echoCancellation: true,
    noiseSuppression: true,
    autoGainControl: true,
  },
};

// Énergie au-dessus de laquelle on considère que quelqu'un parle. Sur un flux
// nettoyé, le bruit de fond d'une pièce reste très bas ; une voix proche
// dépasse largement. Calibré volontairement HAUT : un faux positif coupe la
// réponse au milieu, ce qui est plus agaçant qu'un raté.
const SEUIL = 0.055;
// Il faut que ça DURE : un claquement de porte ou un clic de souris ne coupe
// personne.
const DUREE_MS = 220;
// On ignore le tout début de la parole : la synthèse démarre, l'annulation
// d'écho met un instant à converger, et c'est là qu'elle laisse passer.
const GRACE_MS = 400;

/**
 * Ouvre un micro NETTOYÉ. Rend null si c'est impossible — jamais d'exception.
 * @returns {Promise<{flux:MediaStream, reglages:object, fermer:Function,
 *   surveiller:Function}|null>}
 */
export async function ouvrirMicroPropre() {
  if (
    typeof navigator === "undefined" ||
    !navigator.mediaDevices ||
    typeof navigator.mediaDevices.getUserMedia !== "function"
  ) {
    return null;
  }
  let flux;
  try {
    flux = await navigator.mediaDevices.getUserMedia(CONTRAINTES);
  } catch (e) {
    // Micro refusé, aucun périphérique, contexte non sécurisé : on rend null
    // et l'appelant décide. Une exception ici fermerait la conversation.
    console.error("[micro] ouverture refusée:", e?.name || e);
    return null;
  }

  const piste = flux.getAudioTracks()[0] || null;
  // ⚠️ ON LIT CE QUI A ÉTÉ RÉELLEMENT APPLIQUÉ, pas ce qu'on a demandé. Un
  // navigateur peut accepter la contrainte et ne rien appliquer ; le dire
  // permet de ne pas promettre une annulation qui n'existe pas.
  const reglages = (piste && piste.getSettings && piste.getSettings()) || {};

  let ctx = null;
  let source = null;
  let analyseur = null;
  let arret = null;

  function fermer() {
    if (arret) arret();
    arret = null;
    try {
      if (source) source.disconnect();
      if (ctx && ctx.state !== "closed") ctx.close();
    } catch {
      /* déjà fermé */
    }
    ctx = null;
    source = null;
    analyseur = null;
    try {
      flux.getTracks().forEach((t) => t.stop());
    } catch {
      /* déjà arrêté */
    }
  }

  /**
   * Prévient quand quelqu'un parle. C'est ce qui permet de couper la parole
   * SANS que la reconnaissance soit ouverte — donc sans aucun risque d'écho.
   * @param {() => void} onParole
   * @returns {() => void} de quoi arrêter la surveillance
   */
  function surveiller(onParole) {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return () => {};
    try {
      ctx = new AC();
      source = ctx.createMediaStreamSource(flux);
      analyseur = ctx.createAnalyser();
      // Court : on veut réagir vite, pas analyser un spectre.
      analyseur.fftSize = 1024;
      source.connect(analyseur);
    } catch (e) {
      console.error("[micro] analyse impossible:", e?.message || e);
      return () => {};
    }
    const tampon = new Float32Array(analyseur.fftSize);
    const debut = Date.now();
    let depuis = 0;
    let vivant = true;

    function boucle() {
      if (!vivant || !analyseur) return;
      analyseur.getFloatTimeDomainData(tampon);
      let somme = 0;
      for (let i = 0; i < tampon.length; i += 1) somme += tampon[i] * tampon[i];
      const energie = Math.sqrt(somme / tampon.length);
      const maintenant = Date.now();
      if (maintenant - debut < GRACE_MS) {
        // Fenêtre de grâce : l'annulation d'écho converge.
        depuis = 0;
      } else if (energie > SEUIL) {
        if (!depuis) depuis = maintenant;
        else if (maintenant - depuis >= DUREE_MS) {
          depuis = 0;
          vivant = false;
          try {
            onParole();
          } catch (e) {
            console.error("[micro] surParole", e?.message || e);
          }
          return;
        }
      } else {
        depuis = 0;
      }
      requestAnimationFrame(boucle);
    }
    requestAnimationFrame(boucle);
    arret = () => {
      vivant = false;
    };
    return arret;
  }

  return { flux, reglages, fermer, surveiller };
}

/**
 * L'annulation d'écho est-elle RÉELLEMENT en place sur cette piste ?
 * ⚠️ On lit `getSettings()`, pas la contrainte demandée : un navigateur peut
 * accepter la demande et ne rien appliquer.
 */
export function echoAnnule(reglages) {
  return Boolean(reglages && reglages.echoCancellation === true);
}

export const _internal = { CONTRAINTES, SEUIL, DUREE_MS, GRACE_MS };
